import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Plus, Trash2, KeyRound, RotateCcw } from "lucide-react";

const ROLE_CLS = {
  master: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  admin: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  member: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  viewer: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function Users() {
  const { t, user } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "member" });

  const load = useCallback(() => {
    api.get("/auth/users").then((r) => setItems(r.data)).catch((e) => {
      if (e.response?.status === 403) toast.error(t("users.forbidden"));
    });
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/auth/users", form);
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm({ email: "", name: "", phone: "", role: "member" });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  const resetPwd = async (id) => {
    try {
      await api.post(`/auth/users/${id}/reset-password`);
      toast.success(t("users.resetDone"));
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  const [accessCode, setAccessCode] = useState(null);
  const issueCode = async (u) => {
    try {
      const r = await api.post(`/auth/users/${u.user_id}/access-code`);
      setAccessCode({ ...r.data, email: u.email });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {accessCode && (
        <div className="rounded-md border border-amber-400 bg-amber-500/10 p-4 text-sm space-y-1 text-foreground" data-testid="access-code-box">
          <div className="font-semibold">{t("users.accessCodeTitle")} — {accessCode.email}</div>
          <div className="font-mono text-2xl tracking-widest" data-testid="access-code-value">{accessCode.code}</div>
          <div className="text-muted-foreground">{t("users.accessCodeHelp")}</div>
          <Button size="sm" variant="ghost" onClick={() => setAccessCode(null)} data-testid="access-code-close">{t("common.cancel")}</Button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <KeyRound className="h-7 w-7 text-primary" />{t("nav.users")}
        </h1>
        <Button onClick={() => setOpen(true)} data-testid="new-user-button">
          <Plus className="h-4 w-4 mr-2" />{t("users.add")}
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{t("users.phone")}</TableHead>
                  <TableHead>{t("users.role")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="text-xs">{(u.name || u.email).slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{u.name || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div>{u.phone || "—"}</div>
                      <input className="mt-1 w-32 bg-transparent border-b border-dashed border-border text-[11px] text-emerald-500 outline-none focus:border-primary" defaultValue={u.whatsapp_number || ""} placeholder={`WA: ${t("users.whatsapp")}`}
                        onBlur={(e) => { if (e.target.value !== (u.whatsapp_number || "")) api.put(`/auth/users/${u.user_id}`, { whatsapp_number: e.target.value }).then(() => { toast.success(t("msg.saved")); load(); }).catch(() => toast.error(t("msg.error"))); }}
                        data-testid={`user-whatsapp-${u.user_id}`} />
                    </TableCell>
                    <TableCell><Badge className={ROLE_CLS[u.role]}>{t(`users.${u.role}`)}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={u.has_password ? "default" : "secondary"}>
                        {u.has_password ? t("users.pwdSet") : t("users.pwdPending")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.user_id !== user?.user_id && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" title={t("users.resetPwd")} data-testid={`reset-user-${u.user_id}`} onClick={() => resetPwd(u.user_id)}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title={t("users.accessCode")} data-testid={`access-code-${u.user_id}`} onClick={() => issueCode(u)}>
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u.user_id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("users.add")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("common.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label>{t("users.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39..." data-testid="user-phone-input" /></div>
            <div><Label>{t("users.whatsapp")}</Label>
              <Input value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+39..." data-testid="user-whatsapp-input" /></div>
            <div><Label>{t("users.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t("users.member")}</SelectItem>
                  <SelectItem value="viewer">{t("users.viewer")}</SelectItem>
                  <SelectItem value="admin">{t("users.admin")}</SelectItem>
                  {user?.role === "master" && <SelectItem value="master">{t("users.master")}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">{t("users.hint")}</p>
            <Button className="w-full" onClick={submit} disabled={!form.email.includes("@")} data-testid="user-submit-button">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
